import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Plus, Minus } from "lucide-react";
import { message } from "antd";
import foodApiService from "../../services/foodApiService";

const AddFoodPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get data passed from previous page
  const {
    nextItemId,
    mealNumber,
    fromHomePage,
    uploadedImage: passedImage,
  } = location.state ?? {};

  const [formData, setFormData] = useState({
    foodName: "",
    servingType: "",
    portionSize: "",
    numberOfServings: 1,
  });

  const [isLoading, setIsLoading] = useState(false);

  const servingTypes = ["Bowl", "Cup", "Glass", "Piece", "Other"];

  const portionSizes = ["Small", "Medium", "Large"];

  const handleBack = () => {
    navigate(-1);
  };

  const handleSave = async () => {
    if (!formData.foodName.trim()) {
      message.error("Please enter food name");
      return;
    }
    if (!formData.servingType) {
      message.error("Please select serving type");
      return;
    }
    if (!formData.portionSize) {
      message.error("Please select portion size");
      return;
    }

    setIsLoading(true);

    try {
      // Prepare API payload
      const apiPayload = {
        name: formData.foodName.trim(),
        container: formData.servingType.toLowerCase(),
        size: formData.portionSize.toLowerCase(),
        count: formData.numberOfServings,
      };

      // Call the API
      const response = await foodApiService.addManualFood(apiPayload);

      // message.success("Food item added successfully!");

      // Create food item object to show on scanned-food-detail page
      const newFoodItem = {
        id: Date.now(),
        name: formData.foodName.trim(),
        servingType: formData.servingType,
        portionSize: formData.portionSize,
        servings: formData.numberOfServings,
        serving_description: `${formData.numberOfServings} ${formData.servingType.toLowerCase()} (${formData.portionSize.toLowerCase()})`,
        // Use API response nutrition values or proper defaults
        calorie_content_kcal:
          response.data?.calorie_content_kcal || response.data?.calories || 200,
        protein_content_g: response.data?.protein_content_g || 15,
        carbohydrate_content_g: response.data?.carbohydrate_content_g || 25,
        net_carbs_g: response.data?.net_carbs_g || 20,
        fat_content_g: response.data?.fat_content_g || 8,
        sugar_g: response.data?.sugar_g || 5,
        dietary_fiber_g: response.data?.dietary_fiber_g || 5,
        GI_estimate: response.data?.GI_estimate || 50,
        GL_estimate: response.data?.GL_estimate || 15,
        // Legacy calories field for compatibility
        calories:
          response.data?.calorie_content_kcal || response.data?.calories || 200,
        weight: formData.numberOfServings * 100,
        image: passedImage || "/assets/imagedosa.png",
      };

      // Navigate to scanned-food-detail page with the new food item
      navigate("/glucera/scanned-food-detail", {
        state: {
          newItem: newFoodItem,
          fromAddPage: true,
          mealNumber,
          fromHomePage,
        },
      });
    } catch (error: any) {
      message.error(error.message || "Failed to add food item");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  // Add CSS to constrain dropdown options
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .dropdown-container select option {
        max-width: 100% !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        white-space: nowrap !important;
      }
      .dropdown-container {
        position: relative !important;
        overflow: visible !important;
      }
      .dropdown-container select {
        max-width: 100% !important;
        width: 100% !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div className="mx-auto min-h-screen w-full max-w-[400px] overflow-hidden bg-white">
      <div className="relative space-y-6 overflow-visible p-4">
        {/* Header */}
        <div className="mb-6 flex items-center">
          <button
            onClick={handleBack}
            className="rounded-lg p-2 transition-colors hover:bg-gray-100"
          >
            <ArrowLeft className="h-6 w-6 text-gray-600" />
          </button>
          <h1 className="ml-4 text-lg font-semibold text-gray-900">
            Add Food Item
          </h1>
        </div>

        <div className="space-y-6">
          {/* Food Name */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Food Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.foodName}
              onChange={e =>
                setFormData({ ...formData, foodName: e.target.value })
              }
              className="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-gray-900 placeholder-gray-500 transition-colors hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Grilled Chicken Breast"
            />
          </div>

          {/* Serving Type */}
          <div className="relative z-20 space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Serving Type <span className="text-red-500">*</span>
            </label>
            <div
              className="dropdown-container relative"
              style={{ maxWidth: "100%", overflow: "visible" }}
            >
              <select
                value={formData.servingType}
                onChange={e =>
                  setFormData({ ...formData, servingType: e.target.value })
                }
                className="w-full appearance-none rounded-lg border border-gray-200 bg-white px-4 py-3 text-gray-900 transition-colors hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%234b5563' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: "right 12px center",
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "16px",
                  maxWidth: "100%",
                  width: "100%",
                }}
              >
                <option value="" disabled>
                  Select serving type
                </option>
                {servingTypes.map(type => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Portion Size */}
          <div className="relative z-10 space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Portion Size <span className="text-red-500">*</span>
            </label>
            <div
              className="dropdown-container relative"
              style={{ maxWidth: "100%", overflow: "visible" }}
            >
              <select
                value={formData.portionSize}
                onChange={e =>
                  setFormData({ ...formData, portionSize: e.target.value })
                }
                className="w-full appearance-none rounded-lg border border-gray-200 bg-white px-4 py-3 text-gray-900 transition-colors hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%234b5563' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: "right 12px center",
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "16px",
                  maxWidth: "100%",
                  width: "100%",
                }}
              >
                <option value="" disabled>
                  Select portion size
                </option>
                {portionSizes.map(size => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
            <p className="text-xs text-gray-500">
              Indicate whether your serving is small, medium, or large
            </p>
          </div>

          {/* Number of Servings */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Number of Servings <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center justify-center gap-4">
              {/* Decrease Button */}
              <button
                onClick={() => {
                  if (formData.numberOfServings > 1) {
                    setFormData({
                      ...formData,
                      numberOfServings: formData.numberOfServings - 1,
                    });
                  }
                }}
                className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={formData.numberOfServings <= 1}
              >
                <Minus className="h-4 w-4" />
              </button>

              {/* Servings Display */}
              <div className="min-w-[60px] text-center">
                <span className="text-2xl font-bold text-gray-900">
                  {formData.numberOfServings}
                </span>
              </div>

              {/* Increase Button */}
              <button
                onClick={() => {
                  if (formData.numberOfServings < 50) {
                    setFormData({
                      ...formData,
                      numberOfServings: formData.numberOfServings + 1,
                    });
                  }
                }}
                className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={formData.numberOfServings >= 50}
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3 pt-4">
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="w-full rounded-lg bg-blue-600 py-3 font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-400"
            >
              {isLoading ? "Saving..." : "Save"}
            </button>

            <button
              onClick={handleCancel}
              className="w-full rounded-lg border border-gray-300 bg-white py-3 font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>

          {/* Tip */}
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
            <p className="text-sm text-blue-700">
              <span className="font-medium">Tip:</span> Select the type and size
              of your serving for more accurate nutritional calculations.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddFoodPage;
